import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

export interface BudgetItemRow {
  id?: string;
  procedure_id?: string;
  procedure_name: string;
  procedure_code?: string;
  category_name?: string;
  quantity?: number;
  estimated_sessions?: number;
  unit_price?: number;
  discount_amount?: number;
  total?: number;
  tooth_number?: string;
  tooth_region?: string;
  notes?: string;
  status?: string;
  item_type?: string;
}

export interface BudgetDetail {
  id: string;
  budget_number?: string;
  total?: number;
  subtotal?: number;
  discount_amount?: number;
  items?: BudgetItemRow[];
}

export interface BudgetLinkedReceivable {
  id: string;
  budget_id?: string;
  budget_number?: string;
  budget_total?: number;
  description?: string;
}

const CONSULTATION_ITEM_RE = /consult|avalia[çc][ãa]o|retorno|anamnese|teleconsult/i;

/** Alinhado ao BudgetFormDialog — identifica linha de consulta no orçamento. */
export function isConsultationBudgetItem(item: {
  procedure_name?: string | null;
  category_name?: string | null;
  item_type?: string | null;
}): boolean {
  if (item?.item_type === 'consultation') return true;
  const name = String(item?.procedure_name || '').trim();
  const cat = String(item?.category_name || '').trim();
  if (name.toLowerCase() === 'consulta' || cat.toLowerCase() === 'consulta') return true;
  return CONSULTATION_ITEM_RE.test(name) || CONSULTATION_ITEM_RE.test(cat);
}

export function isConsultationOnlyBudget(budget?: Pick<BudgetDetail, 'items'> | null): boolean {
  const items = budget?.items || [];
  if (!items.length) return true;
  return items.every(isConsultationBudgetItem);
}

export function normalizeBudgetItemRow(item: BudgetItemRow): BudgetItemRow | null {
  const status = String(item?.status || '').toLowerCase();
  if (status === 'cancelled') return null;

  const rawName = String(item?.procedure_name || '').trim();
  const categoryName = String(item?.category_name || '').trim();
  const procedureName = rawName
    || (isConsultationBudgetItem(item) ? 'Consulta' : '')
    || categoryName
    || 'Procedimento';

  return {
    ...item,
    procedure_name: procedureName,
    category_name: categoryName || undefined,
    quantity: item.quantity ?? 1,
    unit_price: Number(item.unit_price) || 0,
    total: Number(item.total ?? (item as { total_price?: number }).total_price) || 0,
  };
}

/** Itens prontos para exibição inline (consulta inclusa). */
export function getBudgetDisplayItems(
  budget?: BudgetDetail | null,
  fallbackTotal = 0,
): BudgetItemRow[] {
  const normalized = (budget?.items || [])
    .map((item) => normalizeBudgetItemRow(item))
    .filter((item): item is BudgetItemRow => Boolean(item?.procedure_name));

  if (normalized.length > 0) return normalized;

  const total = Number(budget?.total) || fallbackTotal || 0;
  if (total <= 0) return [];

  if (isConsultationOnlyBudget(budget)) {
    return [{ procedure_name: 'Consulta', quantity: 1, total }];
  }

  return [{ procedure_name: 'Procedimento', quantity: 1, total }];
}

export const isBudgetTransaction = (acc: Pick<BudgetLinkedReceivable, 'budget_id' | 'description'>) =>
  Boolean(acc.budget_id) || /orçamento|orcamento/i.test(acc.description || '');

/** Sessões/seções previstas no item (pacote multi-sessão). */
export function getBudgetItemSessionCount(item: BudgetItemRow): number {
  const sessions = Number(item.estimated_sessions);
  const qty = Number(item.quantity);
  if (Number.isFinite(sessions) && sessions > 0) return Math.max(1, Math.floor(sessions));
  if (Number.isFinite(qty) && qty > 0) return Math.max(1, Math.floor(qty));
  return 1;
}

/**
 * Itens de orçamento com mais de uma sessão/seção.
 *
 * Não excluímos linhas só pelo nome "Consulta": pacotes como "Consulta Clínica"
 * com 2+ sessões são multi-sessão e devem perguntar walk-in no pagamento.
 */
export function getMultiSessionBudgetItems(budget?: BudgetDetail | null): BudgetItemRow[] {
  return getBudgetDisplayItems(budget).filter(
    (item) => getBudgetItemSessionCount(item) > 1,
  );
}

export const extractBudgetNumberFromDescription = (description?: string): string | null => {
  if (!description) return null;
  const match = description.match(/ORC[-\d]+/i);
  return match ? match[0].toUpperCase() : null;
};

export async function fetchBudgetDetailForReceivable(
  acc: BudgetLinkedReceivable,
): Promise<BudgetDetail | null> {
  let budgetId = acc.budget_id || null;
  if (!budgetId) {
    const budgetNumber = extractBudgetNumberFromDescription(acc.description);
    if (budgetNumber) {
      const searchRes = await axios.get(getApiUrl('/clinic/budgets'), {
        params: { search: budgetNumber, limit: 1 },
      });
      budgetId = searchRes.data?.data?.[0]?.id || null;
    }
  }
  if (!budgetId) return null;

  const res = await axios.get(getApiUrl(`/clinic/budgets/${budgetId}`));
  const raw = (res.data?.data as BudgetDetail | undefined) || null;
  if (!raw) return null;

  const fallbackTotal = Number(acc.budget_total) || 0;
  return {
    ...raw,
    items: getBudgetDisplayItems(raw, fallbackTotal),
  };
}
